import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  IReportComponentInstance,
  IInstructionLookUpEntry,
  ReportComponentType,
  DEFAULT_TOP_BAR_TITLE_STYLES,
  DEFAULT_TOP_BAR_SUBTITLE_STYLES,
  ITopBarComponentInstance,
} from 'src/types/report-designer'
import { generateUUID } from 'src/utils/function'
import { IDropdownDto } from 'src/generated-types/api-result'
import {
  useChangeReportTemplateStatus,
  useCreateReportTemplate,
  useGetReportTemplateById,
  useGetReportTemplateWarnings,
  usePreviewReport,
  useUpdateReportTemplate,
  useUpgradeReportTemplate,
} from 'src/api/report-template'
import { useGetFormSchemaById, useGetFormSchemaColumns } from 'src/api/form-schema'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { buildInitialInstructionLookUp } from './ReportDesignerContainer.action'

export interface UseReportDesignerStateParams {
  customerId: number
  templateId?: EntityId
  templateName?: string
  locations?: number[]
  formSchema?: IDropdownDto
}

export const useReportDesignerState = ({
  customerId,
  templateId,
  templateName,
  locations,
  formSchema,
}: UseReportDesignerStateParams) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const theme = useTheme()
  const isEditMode = !!templateId

  const { data: initialData, isLoading: isTemplateLoading } = useGetReportTemplateById(customerId, templateId ?? 0)
  const { data: formSchemaData } = useGetFormSchemaById(customerId, formSchema?.id ?? 0)
  const { data: formSchemaColumns, isLoading: isColumnsLoading } = useGetFormSchemaColumns(
    customerId,
    initialData?.formSchemaVersionId ?? formSchemaData?.id ?? 0,
  )
  const { data: warnings } = useGetReportTemplateWarnings(
    customerId,
    initialData?.id as EntityId,
    formSchemaData?.formSchemaId as EntityId,
  )
  const { mutateAsync: changeStatus } = useChangeReportTemplateStatus(customerId, initialData?.id ?? 0)
  const { mutateAsync: upgradeTemplate, isLoading: isUpgradeLoading } = useUpgradeReportTemplate(
    customerId,
    initialData?.id ?? 0,
  )
  const { closeAllDialogs, showConfirmDialog, openFilePreviewDialog } = useDialogCtx()
  const { mutateAsync: saveReportTemplate } = useCreateReportTemplate(customerId)
  const { mutateAsync: updateReportTemplate } = useUpdateReportTemplate(customerId, initialData?.id ?? 0)
  const { mutateAsync: previewReport, isLoading: isPreviewLoading } = usePreviewReport(customerId as EntityId)

  const [currentTemplateName, setCurrentTemplateName] = useState<string>(templateName ?? '')
  const [currentLocations, setCurrentLocations] = useState<number[]>((locations ?? []) as number[])

  const [canvasItems, setCanvasItems] = useState<IReportComponentInstance[]>(() => {
    if (!templateId) {
      const topBarId = generateUUID()
      return [
        {
          id: topBarId,
          type: ReportComponentType.TopBar,
          label: translate('top-bar', { ns: 'dynamic-report' }),
          title: translate('report-title', { ns: 'dynamic-report' }),
          subtitle: translate('report-subtitle', { ns: 'dynamic-report' }),
          backgroundColor: theme.palette.primaryDark.main,
          titleStyles: { ...DEFAULT_TOP_BAR_TITLE_STYLES(theme) },
          subtitleStyles: { ...DEFAULT_TOP_BAR_SUBTITLE_STYLES(theme) },
        } as ITopBarComponentInstance,
      ]
    }
    return []
  })

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [instructionLookUp, setInstructionLookUp] = useState<Record<string, IInstructionLookUpEntry>>({})

  useEffect(() => {
    if (!templateId || isTemplateLoading || !initialData) return

    if (initialData.layout) {
      try {
        const parsed = JSON.parse(initialData.layout) as {
          components: IReportComponentInstance[]
          instructionLookUp?: Record<string, IInstructionLookUpEntry>
        }
        setCanvasItems(parsed.components ?? [])
        if (parsed.instructionLookUp && Object.keys(parsed.instructionLookUp).length > 0) {
          setInstructionLookUp(parsed.instructionLookUp)
        }
      } catch {
        toast.error(translate('invalid-layout-data', { ns: 'dynamic-report' }))
      }
    }
    setCurrentTemplateName(initialData.name ?? '')
    setCurrentLocations((initialData.locations ?? []) as number[])
  }, [isTemplateLoading, initialData])

  useEffect(() => {
    if (!formSchemaColumns || Object.keys(instructionLookUp).length > 0) return
    if (templateId && isTemplateLoading) return
    let existingLookUp: Record<string, IInstructionLookUpEntry> | undefined
    if (initialData?.layout) {
      try {
        const parsed = JSON.parse(initialData.layout) as {
          components: IReportComponentInstance[]
          instructionLookUp?: Record<string, IInstructionLookUpEntry>
        }
        existingLookUp = parsed.instructionLookUp
      } catch {
        existingLookUp = undefined
      }
    }
    setInstructionLookUp(buildInitialInstructionLookUp(canvasItems, formSchemaColumns, existingLookUp))
  }, [formSchemaColumns, canvasItems])

  return {
    translate,
    theme,
    isEditMode,
    initialData,
    isTemplateLoading,
    formSchemaData,
    formSchemaColumns,
    isColumnsLoading,
    warnings,
    changeStatus,
    upgradeTemplate,
    closeAllDialogs,
    showConfirmDialog,
    openFilePreviewDialog,
    saveReportTemplate,
    updateReportTemplate,
    previewReport,
    isPreviewLoading,
    canvasItems,
    setCanvasItems,
    selectedItemId,
    setSelectedItemId,
    instructionLookUp,
    setInstructionLookUp,
    currentTemplateName,
    setCurrentTemplateName,
    currentLocations,
    setCurrentLocations,
    isUpgradeLoading,
  }
}
